import { Box, Button, Skeleton, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import {
  ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { sortingFns } from "../../../../shared/utils/tanstack-table";

import { dateFormatter } from "../../../../shared/utils/date-formatter";
import { useEntityLink } from "../../../../shared/hooks/useEntityLink";
import { MergedRow } from "../hooks/useTasksNewData";
import { useTasksNewData } from "../hooks/useTasksNewData";
import { useTasksNewFilters } from "../hooks/useTasksNewFilters";
import { TasksNewStatusBadge } from "./TasksNewStatusBadge";
import TasksNewDocumentsCell from "./TasksNewDocumentsCell";
import TasksNewNotesCell from "./TasksNewNotesCell";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatFullName(
  p: { firstName: string; middleName?: string | null; lastName: string } | null | undefined
): string {
  if (!p) return "—";
  return [p.firstName, p.middleName, p.lastName].filter(Boolean).join(" ") || "—";
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const { accessor } = createColumnHelper<MergedRow>();

function EntityCell({
  entity,
  type,
}: {
  entity:
    | { id: number; firstName: string; middleName?: string | null; lastName: string }
    | null
    | undefined;
  type: "Patient" | "Caregiver";
}) {
  const entityLink = useEntityLink({ redirectModal: false });
  if (!entity) return <>—</>;
  const fullName = formatFullName(entity);
  return (
    <a
      style={{ color: "var(--chakra-colors-blue-600)", cursor: "pointer", textDecoration: "none" }}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        entityLink.open({ event, entity: { id: entity.id as never, type } });
      }}
    >
      {fullName}
    </a>
  );
}

const columns = [
  accessor((row) => row.patient, {
    id: "patient",
    header: "Patient",
    cell: (info) => <EntityCell entity={info.getValue()} type="Patient" />,
  }),
  accessor((row) => row.caregiver, {
    id: "caregiver",
    header: "Caregiver",
    cell: (info) => <EntityCell entity={info.getValue()} type="Caregiver" />,
  }),
  accessor("taskName", {
    header: "Task name",
    cell: (info) => {
      const row = info.row.original;
      const duplicateOfDate = row.__kind === "regular" ? row.duplicateOfDate : null;
      return (
        <Box>
          {info.getValue()}
          {duplicateOfDate != null && (
            <Text color="orange.600" fontSize="xs">
              Duplicated with a task from {dateFormatter.toDate(duplicateOfDate)}
            </Text>
          )}
        </Box>
      );
    },
  }),
  accessor((row) => row.contract?.contractType?.name ?? "—", {
    id: "contract",
    header: "Contract",
    cell: (info) => info.getValue(),
  }),
  accessor("createdAt", {
    header: "Created at",
    cell: (info) => {
      const value = info.getValue();
      return value ? dateFormatter.toDateOrDateTime(value) : "—";
    },
  }),
  accessor((row) => (row.__kind === "regular" ? row.createdByMember : null), {
    id: "createdByMember",
    header: "Created by",
    cell: (info) => formatFullName(info.getValue()),
  }),
  accessor("scheduledDate", {
    id: "scheduledDate",
    header: "Scheduled date",
    cell: (info) => {
      const value = info.getValue();
      return value ? dateFormatter.toDate(value) : "—";
    },
  }),
  accessor((row) => ({ kind: row.__kind, status: row.__kind === "regular" ? row.status : null }), {
    id: "status",
    header: "Status",
    cell: (info) => {
      const { status } = info.getValue();
      // Future rows have no status; render as INCOMPLETED per spec
      const normalizedStatus =
        status === "COMPLETED" || status === "INCOMPLETED" || status === "APPROVED" ? status : null;
      return <TasksNewStatusBadge status={normalizedStatus} />;
    },
  }),
  accessor((row) => (row.__kind === "regular" ? row.clockInTime : null), {
    id: "clockIn",
    header: "Clock in",
    cell: (info) => {
      const value = info.getValue();
      return value ? dateFormatter.toTime(value) : "—";
    },
  }),
  accessor((row) => (row.__kind === "regular" ? row.clockOutTime : null), {
    id: "clockOut",
    header: "Clock out",
    cell: (info) => {
      const value = info.getValue();
      return value ? dateFormatter.toTime(value) : "—";
    },
  }),
  {
    id: "documents",
    header: "Documents",
    cell: (info) => {
      const row = info.row.original;
      if (row.__kind !== "regular") {
        return <Box onClick={(e) => e.stopPropagation()}>—</Box>;
      }
      return (
        <TasksNewDocumentsCell
          caregiverName={row.caregiver ? formatFullName(row.caregiver) : null}
          documents={row.documents.nodes}
          patientId={row.patientId}
          patientName={formatFullName(row.patient)}
          taskInstanceId={row.id}
        />
      );
    },
  } as ColumnDef<MergedRow>,
  {
    id: "notes",
    header: "Notes",
    cell: (info) => {
      const row = info.row.original;
      if (row.__kind !== "regular") {
        return <>—</>;
      }
      return <TasksNewNotesCell notes={row.notes ?? null} taskInstanceId={row.id} />;
    },
  } as ColumnDef<MergedRow>,
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function TasksNewTable() {
  const { rows, isPending, isError, refetch } = useTasksNewData();
  const filters = useTasksNewFilters();

  const sortDirection = filters.getValue("sortDirection") ?? "DESC";

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    sortingFns,
    manualPagination: true,
    manualSorting: true,
  });

  if (isPending) {
    return <Skeleton h="400px" />;
  }

  if (isError) {
    return (
      <Box>
        <Text color="red.500" mb={2}>
          Failed to load tasks.
        </Text>
        <Button size="sm" onClick={refetch}>
          Retry
        </Button>
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box py={10} textAlign="center">
        <Text color="gray.500" mb={3}>
          No tasks match these filters.
        </Text>
        <Button size="sm" variant="outline" onClick={() => filters.resetToDefault()}>
          Clear filters
        </Button>
      </Box>
    );
  }

  return (
    <Box bg="white" overflowX="auto">
      <Table size="sm">
        <Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th
                  key={header.id}
                  cursor={header.id === "scheduledDate" ? "pointer" : undefined}
                  userSelect={header.id === "scheduledDate" ? "none" : undefined}
                  onClick={
                    header.id === "scheduledDate"
                      ? () => {
                          filters.setValues({
                            sortDirection: sortDirection === "ASC" ? "DESC" : "ASC",
                            page: 1,
                          });
                        }
                      : undefined
                  }
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.id === "scheduledDate" ? (sortDirection === "ASC" ? " ▲" : " ▼") : null}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <Tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <Td key={cell.id} fontSize="sm">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}
